import {
  ATLAS_AI_TASK_WORKING,
  MY_TASKS_CHANGED,
  TASK_UNREAD_COUNT_CHANGED,
} from '@common/constants/webSockets'
import { IFile } from '@common/interfaces/box'
import type { ICustomFieldDefinition, ICustomFieldOption } from '@common/interfaces/customField'
import {
  IMyTasksResponse,
  ITask,
  ITaskActivityResponse,
  ITaskComment,
  TaskStatus,
} from '@common/interfaces/task'
import type { IMyTasksChangedEvent } from '@common/interfaces/taskProject'
import { Action, combineReducers } from 'redux'
import { CLIENT_TAB_ID } from '../../helpers/clientTabId'
import { createKeyedReducer, createLoadingDataReducer } from '../../helpers/redux'
import {
  IPlacementMove,
  ISubtaskMove,
  MY_TASKS_PLACEMENT,
  applySubtaskMove,
  reorderSectionsByIds,
  repositionRows,
} from '../../helpers/taskPlacement'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import {
  SET_TASKS_CUSTOM_FIELD_VALUE_FAILURE,
  SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST,
  SET_TASKS_CUSTOM_FIELD_VALUE_SUCCESS,
} from '../customFields/actions'
import {
  DELETE_SUCCESS as FILE_DELETE_SUCCESS,
  UPLOAD_SUCCESS as FILE_UPLOAD_SUCCESS,
} from '../file/actions'
import {
  ADD_TASKS_TO_PROJECT_FAILURE,
  ADD_TASKS_TO_PROJECT_REQUEST,
  ADD_TASKS_TO_PROJECT_SUCCESS,
  ADD_TASK_TO_PROJECT_FAILURE,
  ADD_TASK_TO_PROJECT_REQUEST,
  ADD_TASK_TO_PROJECT_SUCCESS,
  MARK_ALL_PROJECT_TASKS_READ_SUCCESS,
  MOVE_TASK_PLACEMENT_FAILURE,
  MOVE_TASK_PLACEMENT_REQUEST,
  MOVE_TASK_PLACEMENT_SUCCESS,
  REMOVE_TASK_FROM_PROJECT_FAILURE,
  REMOVE_TASK_FROM_PROJECT_REQUEST,
  REMOVE_TASK_FROM_PROJECT_SUCCESS,
} from '../taskProjects/actions'
import { ILoadingData } from '../types'
import * as actions from './actions'
import recurrenceRules from './recurrenceRules/reducer'

// `latestParams` is the params object of the most recent full-list request. The
// middleware puts the same object on the REQUEST and its SUCCESS/FAILURE, so a
// response can be matched to its request by identity. Two list requests can
// overlap (the first render's fetch and the URL-seeded one, or a quick chip
// switch), and an older, slower response must not overwrite the newer list —
// that is how an unfiltered first request once painted everyone's tasks under a
// lit "My Tasks" chip.
type IMyTasksDataState = ILoadingData<IMyTasksResponse | null> & {
  latestParams?: IPaginationParams
}

// ATLAS_AI_TASK_WORKING. Everything but the id is patched straight onto the
// task, so the stop-eligible ids travel with the flag rather than being
// recomputed — `canStopArc` is annotated per read and is absent on a task whose
// flag flipped live, which would leave the working line without its Stop.
type IArcTaskWorkingEvent = {
  taskId: string
  workingUserId: string | null
  workingUserName?: string
  arcStoppableByUserIds?: string[]
  canStopArc?: boolean
}

// Byte order, as Postgres compares ISO timestamps and uuids.
const compareStrings = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

// The server's manual order within a section: completed rows last, then position
// (null in No Section), then creation date and id.
const compareManualOrder = (a: ITask, b: ITask): number =>
  Number(a.status === TaskStatus.Complete) - Number(b.status === TaskStatus.Complete) ||
  (a.myTasksPosition ?? 0) - (b.myTasksPosition ?? 0) ||
  compareStrings(a.createdAt, b.createdAt) ||
  compareStrings(a.id, b.id)

// Rows render as a fold over array order, so only the order within each section matters.
const sortSectionsByManualOrder = (tasks: ITask[]): ITask[] => {
  const bySection = new Map<string | null, ITask[]>()
  tasks.forEach((task) => {
    const key = task.myTasksSectionId ?? null
    const rows = bySection.get(key)
    if (rows) {
      rows.push(task)
    } else {
      bySection.set(key, [task])
    }
  })
  return [...bySection.values()].flatMap((rows) => rows.sort(compareManualOrder))
}

type IPlacementMoveParams = IPlacementMove & { preview?: IPlacementMove[] }

const placementMovesOf = (params: IPlacementMoveParams): IPlacementMove[] =>
  params.preview ?? [params]

// The drop always clears the column sort, so every section is laid out in the manual
// order the follow-up refetch returns.
const applyMyTasksMove = (tasks: ITask[], move: IPlacementMove): ITask[] => {
  const moved = repositionRows(tasks, move, MY_TASKS_PLACEMENT)
  return moved === tasks ? tasks : sortSectionsByManualOrder(moved)
}

type ISubtaskMoveParams = ISubtaskMove & { preview?: ISubtaskMove[] }

const subtaskMovesOf = (params: ISubtaskMoveParams): ISubtaskMove[] => params.preview ?? [params]

// A promoted task lands as a row in the personal section it was dropped on, or unfiled
// when dropped on No Section. A board's section (the same action on the project page)
// leaves it in whatever personal placement it kept from before it was nested.
const applyMyTasksSubtaskMove = (data: IMyTasksResponse, move: ISubtaskMove): IMyTasksResponse => {
  const { rows, detached } = applySubtaskMove(data.tasks, move)
  if (!detached) {
    return rows === data.tasks ? data : { ...data, tasks: rows }
  }
  // That kept placement is also where the server moves it from, so its gap closes too.
  const row: ITask = {
    ...detached,
    myTasksSectionId: detached.myTasksSectionId ?? null,
    myTasksPosition: detached.myTasksPosition ?? null,
  }
  const isOwnSection = data.sections.some((section) => section.id === move.sectionId)
  if (move.sectionId && !isOwnSection) {
    return { ...data, tasks: sortSectionsByManualOrder([...rows, row]) }
  }
  const tasks = applyMyTasksMove([...rows, row], {
    taskId: detached.id,
    sectionId: move.sectionId ?? null,
    position: move.position,
  })
  return { ...data, tasks }
}

// Clears the dot on the loaded rows `isRead` picks, taking them off the view's unread count.
const clearUnreadRows = (
  data: IMyTasksResponse,
  isRead: (task: ITask) => boolean,
): IMyTasksResponse => {
  let clearedCount = 0
  const tasks = data.tasks.map((task) => {
    if (!task.isUnread || !isRead(task)) {
      return task
    }
    clearedCount += 1
    return { ...task, isUnread: false }
  })
  if (clearedCount === 0) {
    return data
  }
  const unread = Math.max(0, (data.counts?.unread ?? 0) - clearedCount)
  return { ...data, tasks, counts: { ...data.counts, unread } }
}

const myTasksDataInitialState: IMyTasksDataState = {
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: null,
}

// Load-more pages are exempt. A page from a superseded query can still be
// appended after a filter switch, but that predates this guard and is left as
// is: closing it means matching pages by query and making the page's promise
// reject when dropped, which is its own change.
const isStaleListResponse = (
  state: IMyTasksDataState,
  action: IActionWithParams<IPaginationParams>,
): boolean =>
  !action.params?.loadMore && !!state.latestParams && action.params !== state.latestParams

const myTasksData = (
  state: IMyTasksDataState = myTasksDataInitialState,
  action: IActionWithParams<IPaginationParams>,
): IMyTasksDataState => {
  switch (action.type) {
    // Arc started or finished working one of the listed tasks. Patched in place
    // rather than refetched: the event fans out to every internal user, and a
    // refetch per flip would be a list query per viewer per turn. Subtasks are
    // patched too — Arc can be assigned one.
    case ATLAS_AI_TASK_WORKING: {
      if (!state.data?.tasks) {
        return state
      }
      const { taskId, ...arcPatch } = action.data as IArcTaskWorkingEvent
      let didChange = false
      const tasks = state.data.tasks.map((task) => {
        const subtasks = task.subtasks?.map((subtask) =>
          subtask.id === taskId ? { ...subtask, ...arcPatch } : subtask,
        )
        const subtasksChanged = subtasks?.some((subtask, i) => subtask !== task.subtasks?.[i])
        if (task.id === taskId || subtasksChanged) {
          didChange = true
          return {
            ...task,
            ...(task.id === taskId ? arcPatch : {}),
            ...(subtasksChanged ? { subtasks } : {}),
          }
        }
        return task
      })
      // A no-op event (a task this viewer hasn't loaded) must not produce a new
      // array, or every row re-renders on someone else's task.
      if (!didChange) {
        return state
      }
      return { ...state, data: { ...state.data, tasks } }
    }

    case actions.LIST_MY_TASKS_REQUEST:
      if (action.params?.loadMore) {
        return state
      }
      if (action.params?.skipLoader) {
        return { ...state, latestParams: action.params }
      }
      return { ...state, isLoading: true, latestParams: action.params }

    // Optimistic: the row lands where it was dropped before the write returns. The
    // quiet refetch that follows only confirms it. A fresh `latestParams` marks any
    // list response already in flight as stale, so it can't undo the move on arrival.
    case actions.MOVE_MY_TASKS_PLACEMENT_REQUEST: {
      const params = action.params as unknown as IPlacementMoveParams | undefined
      if (!params || !state.data) {
        return state
      }
      const tasks = placementMovesOf(params).reduce(applyMyTasksMove, state.data.tasks)
      // A task not in the list yet (just created inline) has nothing to move on screen.
      if (tasks === state.data.tasks) {
        return state
      }
      return { ...state, latestParams: {}, data: { ...state.data, tasks } }
    }

    // Optimistic, like a placement move: shown on REQUEST, confirmed by the refetch.
    case actions.MOVE_SUBTASK_REQUEST: {
      const params = action.params as unknown as ISubtaskMoveParams | undefined
      if (!params || !state.data) {
        return state
      }
      const data = subtaskMovesOf(params).reduce(applyMyTasksSubtaskMove, state.data)
      return data === state.data ? state : { ...state, latestParams: {}, data }
    }

    case actions.REORDER_MY_TASKS_SECTIONS_REQUEST: {
      const sectionIds = (action.params as { sectionIds?: string[] } | undefined)?.sectionIds
      if (!sectionIds || !state.data) {
        return state
      }
      const sections = reorderSectionsByIds(state.data.sections, sectionIds)
      return { ...state, latestParams: {}, data: { ...state.data, sections } }
    }

    case actions.LIST_MY_TASKS_SUCCESS: {
      if (isStaleListResponse(state, action)) {
        return state
      }
      const prev = state.data
      const data = action.data as IMyTasksResponse
      // Carry already-loaded subtask rows across a list refresh. A post-edit
      // refetch (skipLoader) would otherwise blank each parent's lazily-loaded
      // `subtasks` → undefined → the subtask skeleton flashes. Counts come from
      // the fresh payload; the rows themselves are refreshed quietly via
      // listSubtasks (see refetchMyTasks).
      const carrySubtasks = (incoming: IMyTasksResponse): IMyTasksResponse => {
        if (!prev) {
          return incoming
        }
        const prevSubtasks = new Map<string, ITask[] | undefined>(
          prev.tasks.map((task) => [task.id, task.subtasks]),
        )
        const carry = (task: ITask): ITask =>
          prevSubtasks.get(task.id) !== undefined
            ? { ...task, subtasks: prevSubtasks.get(task.id) }
            : task
        return { ...incoming, tasks: incoming.tasks.map(carry) }
      }

      if (!action.params?.loadMore || !state.data) {
        return { ...state, isLoading: false, data: carrySubtasks(data) }
      }
      return {
        ...state,
        isLoading: false,
        data: carrySubtasks({
          ...data,
          tasks: [...state.data.tasks, ...data.tasks],
        }),
      }
    }

    case actions.LIST_MY_TASKS_FAILURE:
      if (isStaleListResponse(state, action)) {
        return state
      }
      return { ...state, isLoading: false }

    // Optimistic: drop the unread dot the moment we report the read, so the list
    // doesn't keep flagging the task the user is looking at in the panel.
    case actions.MARK_TASK_READ_REQUEST: {
      const taskId = (action.params as { taskId?: string } | undefined)?.taskId
      if (!state.data || !taskId) {
        return state
      }
      const data = clearUnreadRows(state.data, (task) => task.id === taskId)
      return data === state.data ? state : { ...state, data }
    }

    // Optimistic, like a single read. The server clears every page of the view,
    // so the view's count goes to zero, not just down by the loaded rows.
    case actions.MARK_ALL_TASKS_READ_REQUEST: {
      if (!state.data) {
        return state
      }
      const data = clearUnreadRows(state.data, () => true)
      return { ...state, data: { ...data, counts: { ...data.counts, unread: 0 } } }
    }

    // Lazy-loaded subtasks fold into their parent's `subtasks` array. The
    // generic `nestedRows` helper assumes a `{ data: [] }` slice,
    // so we merge explicitly into this `{ tasks, counts, pagination }` shape.
    case actions.LIST_SUBTASKS_SUCCESS: {
      const params = action.params as { parentTaskId?: string; loadMore?: boolean } | undefined
      const parentId = params?.parentTaskId
      if (!state.data || !parentId) {
        return state
      }
      const payload = action.data as { data: ITask[] } | undefined
      const rows: ITask[] = payload?.data ?? []
      const foldSubtasks = (task: ITask): ITask =>
        task.id === parentId
          ? {
              ...task,
              subtasks: params?.loadMore ? [...(task.subtasks ?? []), ...rows] : rows,
            }
          : task
      return {
        ...state,
        data: {
          ...state.data,
          tasks: state.data.tasks.map(foldSubtasks),
        },
      }
    }

    case actions.CREATE_TASK_REQUEST:
    case actions.UPDATE_TASK_REQUEST:
    case actions.BULK_UPDATE_TASKS_REQUEST:
    case actions.BULK_DELETE_TASKS_REQUEST:
    case actions.MERGE_TASKS_REQUEST:
    case actions.DELETE_TASK_REQUEST:
    case ADD_TASK_TO_PROJECT_REQUEST:
    case ADD_TASKS_TO_PROJECT_REQUEST:
    case SET_TASKS_CUSTOM_FIELD_VALUE_REQUEST:
    case MOVE_TASK_PLACEMENT_REQUEST:
    case REMOVE_TASK_FROM_PROJECT_REQUEST:
      return { ...state, isSaving: true, isSaved: false }

    case actions.CREATE_TASK_SUCCESS:
    case actions.UPDATE_TASK_SUCCESS:
    case actions.BULK_UPDATE_TASKS_SUCCESS:
    case actions.BULK_DELETE_TASKS_SUCCESS:
    case actions.MERGE_TASKS_SUCCESS:
    case actions.DELETE_TASK_SUCCESS:
    case ADD_TASK_TO_PROJECT_SUCCESS:
    case ADD_TASKS_TO_PROJECT_SUCCESS:
    case SET_TASKS_CUSTOM_FIELD_VALUE_SUCCESS:
    case MOVE_TASK_PLACEMENT_SUCCESS:
    case REMOVE_TASK_FROM_PROJECT_SUCCESS:
      return { ...state, isSaving: false, isSaved: true }

    case actions.CREATE_TASK_FAILURE:
    case actions.UPDATE_TASK_FAILURE:
    case actions.BULK_UPDATE_TASKS_FAILURE:
    case actions.BULK_DELETE_TASKS_FAILURE:
    case actions.MERGE_TASKS_FAILURE:
    case actions.DELETE_TASK_FAILURE:
    case ADD_TASK_TO_PROJECT_FAILURE:
    case ADD_TASKS_TO_PROJECT_FAILURE:
    case SET_TASKS_CUSTOM_FIELD_VALUE_FAILURE:
    case MOVE_TASK_PLACEMENT_FAILURE:
    case REMOVE_TASK_FROM_PROJECT_FAILURE:
      return { ...state, isSaving: false, isSaved: false }

    default:
      return state
  }
}

// Sidebar preview reads/writes a separate slice so its 4-item, current-user-only
// fetch can't overwrite the full /my-tasks page list (and vice versa).
const myTasksPreviewData = createLoadingDataReducer<IMyTasksResponse | null>([
  actions.LIST_MY_TASKS_PREVIEW_REQUEST,
])

// The client page's Tasks tab (see ClientTasksContainer) — its own slice for the
// same reason as the preview above.
export interface IClientTasksState extends ILoadingData<IMyTasksResponse | null> {
  // Which client the rows in `data` belong to. Without it the slice is just
  // "some client's tasks", and the tab has no way to tell its own response from
  // the previous client's.
  clientName: string | null
}

interface IClientTasksParams extends IPaginationParams {
  filters?: { clientName?: string[] }
}

const clientTasksInitialState: IClientTasksState = {
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: null,
  clientName: null,
}

const requestedClientName = (action: IActionWithParams<IClientTasksParams>): string | null =>
  action.params?.filters?.clientName?.[0] ?? null

// Custom (not createLoadingDataReducer) for two reasons the factory can't cover:
//
// 1. Client scoping. The factory keeps `data` on _REQUEST, so switching clients
//    with the Tasks tab open would keep rendering — and deep-linking into — the
//    previous client's tasks until the new response landed. Worse, two responses
//    can arrive out of order and strand the wrong client's rows here for good.
//    So: drop the rows the moment a different client is requested, and ignore
//    any SUCCESS/FAILURE that doesn't belong to the client we're now showing.
// 2. loadMore. The factory only appends when the payload has a top-level `data`
//    array; this one is `{ tasks, counts, pagination }`, so it
//    would silently replace instead of append (same reason myTasksData above is
//    hand-written).
const clientTasksData = (
  state: IClientTasksState = clientTasksInitialState,
  action: IActionWithParams<IClientTasksParams>,
): IClientTasksState => {
  switch (action.type) {
    case actions.LIST_CLIENT_TASKS_REQUEST: {
      const clientName = requestedClientName(action)
      if (clientName !== state.clientName) {
        return { ...clientTasksInitialState, isLoading: true, clientName }
      }
      // Paging within the current client — keep the rows on screen, the
      // InfiniteScroll renders its own footer loader.
      if (action.params?.loadMore) {
        return state
      }
      return { ...state, isLoading: true }
    }

    case actions.LIST_CLIENT_TASKS_SUCCESS: {
      if (requestedClientName(action) !== state.clientName) {
        return state
      }
      const data = action.data as IMyTasksResponse
      if (!action.params?.loadMore || !state.data) {
        return { ...state, isLoading: false, data }
      }
      return {
        ...state,
        isLoading: false,
        data: { ...data, tasks: [...state.data.tasks, ...data.tasks] },
      }
    }

    case actions.LIST_CLIENT_TASKS_FAILURE:
      if (requestedClientName(action) !== state.clientName) {
        return state
      }
      return { ...state, isLoading: false }

    default:
      return state
  }
}

export const taskDetailInitialState: ILoadingData<ITask | null> = {
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: null,
}

// Every task a drawer has loaded, keyed by id (`params.taskDetailId`). Several drawers can be
// mounted at once (a page's panel plus the Messages module panel), and one shared slot let
// whichever loaded last strand the other on its skeleton. Inline images make a task heavy,
// hence the cap; a drawer whose task is dropped loads it again.
const TASK_DETAIL_CACHE_SIZE = 10

// Custom (not createLoadingDataReducer) because the factory's live-edit only
// folds updates into array-shaped slices, and this holds a single task. The
// UPDATE_TASK_SUCCESS branch keeps an open detail panel in sync with edits made
// anywhere (e.g. completing from the list row) without a re-fetch.
const taskDetail = (
  state: ILoadingData<ITask | null> = taskDetailInitialState,
  action: IAction,
): ILoadingData<ITask | null> => {
  switch (action.type) {
    // Keeps the open panel's working line in step with the row's.
    case ATLAS_AI_TASK_WORKING: {
      const { taskId, ...arcPatch } = action.data as IArcTaskWorkingEvent
      if (state.data?.id !== taskId) {
        return state
      }
      return { ...state, data: { ...state.data, ...arcPatch } }
    }

    case actions.SHOW_TASK_REQUEST:
      return { ...state, isLoading: true }

    case actions.SHOW_TASK_SUCCESS:
      return { ...state, isLoading: false, data: action.data as ITask }

    case actions.SHOW_TASK_FAILURE:
      return { ...state, isLoading: false }

    // The open panel's Subtasks list follows a move the same way the list does.
    case actions.MOVE_SUBTASK_REQUEST: {
      const params = action.params as unknown as ISubtaskMoveParams | undefined
      if (!params || !state.data) {
        return state
      }
      const [task] = subtaskMovesOf(params).reduce<ITask[]>(
        (rows, move) => {
          const next = applySubtaskMove(rows, move).rows
          // Never lose the open task itself, e.g. when it is the one being nested.
          return next.length === 1 ? next : rows
        },
        [state.data],
      )
      return task === state.data ? state : { ...state, data: task }
    }

    // The panel's My Tasks section picker follows a move from either the picker or a drag.
    case actions.MOVE_MY_TASKS_PLACEMENT_REQUEST: {
      const params = action.params as unknown as IPlacementMoveParams | undefined
      if (!params || !state.data) {
        return state
      }
      const openTaskId = state.data.id
      const move = placementMovesOf(params).find(({ taskId }) => taskId === openTaskId)
      if (!move) {
        return state
      }
      return { ...state, data: { ...state.data, myTasksSectionId: move.sectionId } }
    }

    case actions.UPDATE_TASK_SUCCESS: {
      const updatePayload = action.data as (ITask & { success?: boolean }) | undefined
      if (!state.data || !updatePayload?.id) {
        return state
      }
      // Drop the `success` envelope the service appends so it doesn't pollute
      // the task object (showTask populates the entry without it).
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit `success`, keep task fields
      const { success, ...taskData } = updatePayload

      // The open task itself was updated — merge (not replace) so relations the
      // shallow update payload omits (subtasks, deep activity/reporting files)
      // survive from the last showTask.
      if (taskData.id === state.data.id) {
        return { ...state, data: { ...state.data, ...taskData } }
      }

      // A subtask of the open task was updated (e.g. completed from the list
      // row) — fold it into the parent's subtasks[] so the panel's Subtasks
      // section reflects it without a re-fetch.
      if (taskData.parentTaskId === state.data.id && state.data.subtasks) {
        return {
          ...state,
          data: {
            ...state.data,
            subtasks: state.data.subtasks.map((subtask): ITask =>
              subtask.id === taskData.id ? { ...subtask, ...taskData } : subtask,
            ),
          },
        }
      }

      return state
    }

    // Keep the open panel's Files list in sync with attachment uploads/deletes
    // by folding the mutation response into the task's files — no full task
    // refetch (which would also re-hydrate every inline image from S3). Mirrors
    // the UPDATE_TASK_SUCCESS store-merge above.
    case FILE_UPLOAD_SUCCESS: {
      if (!state.data) {
        return state
      }
      const taskId = state.data.id
      const uploaded = (action.data as { files?: IFile[] } | undefined)?.files ?? []
      const taskFiles = uploaded.filter((file) => (file as { taskId?: string }).taskId === taskId)
      if (taskFiles.length === 0) {
        return state
      }
      return {
        ...state,
        data: { ...state.data, files: [...(state.data.files ?? []), ...taskFiles] },
      }
    }

    case FILE_DELETE_SUCCESS: {
      const ids = (action as { params?: { ids?: string[] } }).params?.ids
      if (!state.data?.files || !ids?.length) {
        return state
      }
      const remaining = state.data.files.filter((file) => !ids.includes(file.id))
      if (remaining.length === state.data.files.length) {
        return state
      }
      return { ...state, data: { ...state.data, files: remaining } }
    }

    default:
      return state
  }
}

// Records the params of the most recent (non-loadMore) list fetch so an
// Arc-write refetch saga can silently reload the same list — the page's filter/
// order/pagination state lives in component state, not redux, so it's captured
// here at dispatch time.
const lastListParams = (
  state: IPaginationParams | null = null,
  action: IActionWithParams<IPaginationParams>,
): IPaginationParams | null => {
  switch (action.type) {
    case actions.LIST_MY_TASKS_REQUEST:
      if (action.params?.loadMore) {
        return state
      }
      return action.params ?? {}
    default:
      return state
  }
}

// Backs the Tasks nav badge: how many assigned tasks the user has never opened.
// Seeded once per session by countUnreadTasks (see MainLayout.tsx), then kept live
// by the TASK_UNREAD_COUNT_CHANGED WebSocket event (and the Mark all read reply) —
// the server already scopes delivery to the assignee's own sockets (see
// TaskUnreadCountSubscriber), so the payload's count is trusted as-is without an
// extra userId check here. Every path carries the same server-computed number,
// so they can't disagree.
const unreadTaskCount = (state = 0, action: IAction<{ unreadCount?: number | null }>): number => {
  switch (action.type) {
    case actions.COUNT_UNREAD_TASKS_SUCCESS:
    case TASK_UNREAD_COUNT_CHANGED:
      return action.data?.unreadCount ?? 0
    // Null when the server couldn't recount; the socket event then corrects it.
    case actions.MARK_ALL_TASKS_READ_SUCCESS:
    case MARK_ALL_PROJECT_TASKS_READ_SUCCESS:
      return action.data?.unreadCount ?? state
    default:
      return state
  }
}

// A task, its comments and its activity share one entry, so they're kept or evicted together.
const taskDetailEntry = combineReducers({
  task: taskDetail,
  comments: createLoadingDataReducer<{ data: ITaskComment[]; total: number } | null>(
    [actions.LIST_TASK_COMMENTS_REQUEST],
    [actions.CREATE_TASK_COMMENT_REQUEST],
  ),
  activity: createLoadingDataReducer<ITaskActivityResponse | null>([
    actions.LIST_TASK_ACTIVITY_REQUEST,
  ]),
})

export type ITaskDetailEntry = ReturnType<typeof taskDetailEntry>

// Unkeyed actions (a subtask move, an edit from the list row, a file upload) reach every
// loaded task, and each entry decides whether it's about it.
const taskDetailsById = createKeyedReducer(taskDetailEntry, 'taskDetailId', {
  fanOutUnkeyed: true,
  maxKeys: TASK_DETAIL_CACHE_SIZE,
})

// Category is a single-select custom field — the endpoint returns its definition
// (the id is how writes and filters address it) with the selectable options
// (id + name + colour), not bare label strings. `id` is null until the seed has run.
const categories = createLoadingDataReducer<
  ICustomFieldDefinition | { id: null; options: ICustomFieldOption[] } | null
>([actions.LIST_TASK_CATEGORIES_REQUEST])

// The latest task change someone else made, or a resync after a dropped socket.
// `revision` is what an open My Tasks page reacts to.
export interface ILiveMyTasksChange extends Omit<IMyTasksChangedEvent, 'originTabId'> {
  revision: number
  isResync: boolean
}

const liveMyTasksChange = (
  state: ILiveMyTasksChange | null = null,
  action: Action<string> & { data?: IMyTasksChangedEvent },
): ILiveMyTasksChange | null => {
  const revision = (state?.revision ?? 0) + 1
  if (action.type === MY_TASKS_CHANGED && action.data) {
    const { originTabId, ...change } = action.data
    // This tab's own write: it already refetched when the request returned.
    if (originTabId && originTabId === CLIENT_TAB_ID) {
      return state
    }
    return { ...change, revision, isResync: false }
  }
  if (action.type === actions.MY_TASKS_UPDATES_RESYNC) {
    return { taskIds: [], deletedTaskIds: [], involvedUserIds: [], revision, isResync: true }
  }
  return state
}

export default combineReducers({
  liveMyTasksChange,
  myTasksData,
  myTasksPreviewData,
  clientTasksData,
  lastListParams,
  taskDetailsById,
  categories,
  recurrenceRules,
  unreadTaskCount,
})
